import { useMemo, useState } from 'react';
import { format, parse, getDay, addDays } from 'date-fns';
import { Activity } from 'lucide-react';

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const EMPTY_COLOR = '#f3f4f6';

/** Monday-first row index: 0=Mon … 6=Sun */
function mondayRowIndex(date: Date): number {
  const d = getDay(date);
  return d === 0 ? 6 : d - 1;
}

function startOfWeekMonday(date: Date): Date {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  const offset = mondayRowIndex(d);
  d.setDate(d.getDate() - offset);
  return d;
}

function parseLocalDate(isoDate: string): Date {
  return parse(isoDate, 'yyyy-MM-dd', new Date());
}

function heatColor(count: number, max: number): string {
  if (count === 0) return EMPTY_COLOR;
  const t = count / max;
  if (t < 0.25) return '#c7d2fe';
  if (t < 0.5) return '#818cf8';
  if (t < 0.75) return '#6366f1';
  return '#4338ca';
}

interface FlashcardStudyHeatmapProps {
  heatmap: { date: string; count: number }[];
}

const FlashcardStudyHeatmap = ({ heatmap }: FlashcardStudyHeatmapProps) => {
  const [hovered, setHovered] = useState<{ date: string; count: number } | null>(null);

  const { grid, maxCount, totalReviews, busiest, monthTicks } = useMemo(() => {
    if (heatmap.length === 0) {
      return {
        grid: [] as ({ date: string; count: number } | null)[][],
        maxCount: 1,
        totalReviews: 0,
        busiest: null as { date: string; count: number } | null,
        monthTicks: [] as { col: number; label: string }[],
      };
    }

    const countByDate = new Map(heatmap.map((h) => [h.date, h.count]));
    const rangeStartKey = heatmap[0].date;
    const rangeEndKey = heatmap[heatmap.length - 1].date;
    let weekStart = startOfWeekMonday(parseLocalDate(rangeStartKey));

    const cols: ({ date: string; count: number } | null)[][] = [];
    const rangeEndDate = parseLocalDate(rangeEndKey);

    while (weekStart <= rangeEndDate) {
      const column: ({ date: string; count: number } | null)[] = Array(7).fill(null);
      for (let row = 0; row < 7; row++) {
        const day = addDays(weekStart, row);
        const key = format(day, 'yyyy-MM-dd');
        if (key < rangeStartKey || key > rangeEndKey) continue;
        if (countByDate.has(key)) {
          column[row] = { date: key, count: countByDate.get(key)! };
        }
      }
      cols.push(column);
      weekStart = addDays(weekStart, 7);
    }

    let maxCount = 1;
    let totalReviews = 0;
    let busiest: { date: string; count: number } | null = null;
    for (const h of heatmap) {
      totalReviews += h.count;
      if (h.count > maxCount) maxCount = h.count;
      if (h.count > 0 && (!busiest || h.count > busiest.count)) busiest = h;
    }

    const monthTicks: { col: number; label: string }[] = [];
    let lastMonth = '';
    cols.forEach((column, colIdx) => {
      const firstInCol = column.find((c) => c != null);
      if (!firstInCol) return;
      const month = format(parseLocalDate(firstInCol.date), 'MMM');
      if (month !== lastMonth) {
        monthTicks.push({ col: colIdx, label: month });
        lastMonth = month;
      }
    });

    return { grid: cols, maxCount, totalReviews, busiest, monthTicks };
  }, [heatmap]);

  const activeCell = hovered ?? busiest;
  const cellSize = 14;
  const cellGap = 3;

  return (
    <div className="bg-white/80 backdrop-blur rounded-2xl p-4 sm:p-6 shadow-lg border border-white/60">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 mb-1">
        <h3 className="text-sm font-black text-gray-700 uppercase tracking-wide flex items-center gap-2">
          <Activity className="w-4 h-4 text-indigo-500" />
          Study activity
        </h3>
        <p className="text-xs text-gray-500 font-medium">
          {totalReviews} review{totalReviews !== 1 ? 's' : ''} · last 12 weeks
        </p>
      </div>
      <p className="text-xs text-gray-500 mb-4">
        Each square is how many cards you rated that day. Darker = more reviews.
      </p>

      <div className="overflow-x-auto pb-1">
        <div className="inline-flex flex-col min-w-0">
          {monthTicks.length > 0 && (
            <div
              className="flex gap-[3px] ml-9 mb-1 h-4 relative"
              style={{ width: grid.length * (cellSize + cellGap) - cellGap }}
            >
              {monthTicks.map((tick) => (
                <span
                  key={`${tick.col}-${tick.label}`}
                  className="absolute text-[10px] font-bold text-gray-400"
                  style={{ left: tick.col * (cellSize + cellGap) }}
                >
                  {tick.label}
                </span>
              ))}
            </div>
          )}

          <div className="flex gap-[3px]">
            <div className="flex flex-col gap-[3px] pr-1.5 shrink-0">
              {DAY_LABELS.map((label, i) => (
                <span
                  key={label}
                  className="text-[9px] font-semibold text-gray-400 w-7 text-right leading-[14px]"
                  style={{
                    height: cellSize,
                    visibility: i % 2 === 0 ? 'visible' : 'hidden',
                  }}
                >
                  {label}
                </span>
              ))}
            </div>

            <div className="flex gap-[3px]">
              {grid.map((column, colIdx) => (
                <div key={colIdx} className="flex flex-col gap-[3px]">
                  {column.map((cell, rowIdx) => {
                    const count = cell?.count ?? 0;
                    const inRange = cell != null;
                    return (
                      <div
                        key={`${colIdx}-${rowIdx}`}
                        className="rounded-[3px] flex-shrink-0"
                        style={{
                          width: cellSize,
                          height: cellSize,
                          backgroundColor: inRange
                            ? heatColor(count, maxCount)
                            : 'transparent',
                        }}
                        onMouseEnter={() => cell && setHovered(cell)}
                        onMouseLeave={() => setHovered(null)}
                        title={
                          cell
                            ? `${format(parseLocalDate(cell.date), 'EEE, d MMM yyyy')}: ${cell.count} review${cell.count !== 1 ? 's' : ''}`
                            : undefined
                        }
                      />
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap items-center justify-between gap-3 pt-3 border-t border-gray-100">
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-bold text-gray-400 uppercase">Less</span>
          {[0, 0.25, 0.5, 0.75, 1].map((level) => (
            <div
              key={level}
              className="rounded-[3px]"
              style={{
                width: cellSize,
                height: cellSize,
                backgroundColor:
                  level === 0 ? EMPTY_COLOR : heatColor(Math.max(1, level * maxCount), maxCount),
              }}
            />
          ))}
          <span className="text-[10px] font-bold text-gray-400 uppercase">More</span>
        </div>

        {activeCell && activeCell.count > 0 && (
          <p className="text-xs font-semibold text-gray-600">
            {hovered ? (
              <>
                {format(parseLocalDate(activeCell.date), 'EEE, d MMM')}:{' '}
                <span className="text-indigo-600">{activeCell.count} reviews</span>
              </>
            ) : (
              <>
                Busiest day{' '}
                <span className="text-indigo-600">
                  {format(parseLocalDate(activeCell.date), 'd MMM')} ({activeCell.count})
                </span>
              </>
            )}
          </p>
        )}
      </div>
    </div>
  );
};

export default FlashcardStudyHeatmap;
